import { useContext, useEffect, useState } from "react";

import { getExtensionValue, getTypedPropertyValue, TypedValue } from "@medplum/core";
import {
  CodeableConcept,
  QuestionnaireItem,
  QuestionnaireItemInitial,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
  ValueSetExpansionContains,
} from "@medplum/fhirtypes";

import { Switch } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import CodingInputWithDetails from "@/components/CodingInputWithDetails";
import ValueSetSearchInput from "@/components/ValueSetSearchInput";
import { Extensions } from "@/fhir/enums/extensions";
import { setFormFieldValue } from "@/intake-forms/utils/intake-forms";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { type ExtensibleResource, getExtensionValues } from "@/utils/medplum";
import { GenericFormValues } from "@/utils/questionnaires";

import QuestionnaireCheckboxField from "../QuestionnaireCheckboxField";
import QuestionnaireMultiSelectField from "../QuestionnaireMultiSelectField";
import QuestionnaireNativeDropdown from "../QuestionnaireNativeSelect";
import QuestionnaireRadioInputField from "../QuestionnaireRadioInputField";
import QuestionnaireSignatureSelectField from "../QuestionnaireSignatureSelectField";
import QuestionnaireSlider from "../QuestionnaireSlider";
import QuestionnaireValueSetCheckboxField from "../QuestionnaireValueSetCheckboxField";

import { QuestionnaireCodingFilterContext } from "./QuestionnaireCodingFilterContext";

/**
 * Choice ValueSets that use the live form `value` (controlled) for billing subscriber
 * relationship fields. Keeps Spouse/Self in sync when EnableWhen remounts the field.
 */
const FORM_CONTROLLED_VALUE_SET_LINK_IDS = new Set([
  "billingInformationSubscriberRelationship",
  "billingInformationSecondInsuranceSubscriberRelationship",
  "billingInformationThirdInsuranceSubscriberRelationship",
]);

interface QuestionnaireChoiceInputProps<T> {
  readonly name: string;
  readonly label: string;
  readonly item: QuestionnaireItem;
  readonly initial: QuestionnaireItemInitial | undefined;
  readonly response?: QuestionnaireResponseItem;
  form: UseFormReturnType<T>;
  readonly onChangeAnswer: (
    newResponseAnswer: QuestionnaireResponseItemAnswer | QuestionnaireResponseItemAnswer[],
  ) => void;
}

enum FormControlType {
  CheckBox = "check-box",
  RadioButton = "radio-button",
  Slider = "slider",
  NativeSelect = "native-select",
  SignatureSelect = "signature-select",
  Switch = "switch",
}

interface QuestionnaireChoiceFieldProps<T> {
  name: string;
  label: string;
  placeholder?: string;
  questionnaireItem: QuestionnaireItem;
  group?: QuestionnaireItem;
  response?: QuestionnaireResponseItem;
  form: UseFormReturnType<T>;
  value?: GenericFormValues;
  formFieldName?: string;
  hasCodingInputDetails?: boolean;
  valueSetIsCreatable?: boolean;
  onChangeAnswer: (
    newResponseAnswer: QuestionnaireResponseItemAnswer | QuestionnaireResponseItemAnswer[],
  ) => void;
}

function QuestionnaireChoiceField<T>({
  questionnaireItem,
  group,
  name,
  onChangeAnswer,
  response,
  label,
  form,
  value,
  placeholder,
  formFieldName,
  hasCodingInputDetails,
  valueSetIsCreatable,
}: QuestionnaireChoiceFieldProps<T>) {
  const codingFilter = useContext(QuestionnaireCodingFilterContext)?.[questionnaireItem.linkId];
  const formsetLinkId = getExtensionValue(
    questionnaireItem,
    Extensions.QuestionnaireItemFormsetController,
  ) as string | undefined;

  if (formsetLinkId && questionnaireItem.answerValueSet)
    return (
      <QuestionnaireMultiSelectField
        form={form}
        label={label}
        group={questionnaireItem}
        name={formFieldName ?? name}
        formsetLinkId={formsetLinkId}
      />
    );

  const formControlCodeableConcept = getExtensionValue(
    questionnaireItem,
    Extensions.QuestionnaireItemControl,
  ) as CodeableConcept | undefined;
  const formControlType = formControlCodeableConcept?.coding?.[0]?.code;

  // Check if this field is part of a horizontal group
  const isInHorizontalGroup = formFieldName?.includes(".");
  const computedName = isInHorizontalGroup && formFieldName ? formFieldName : name;

  if (formControlType === FormControlType.Slider && !questionnaireItem.answerValueSet) {
    return (
      <QuestionnaireSlider
        form={form}
        label={label}
        name={computedName}
        group={questionnaireItem}
      />
    );
  }

  if (formControlType === FormControlType.CheckBox && !questionnaireItem.answerValueSet)
    return (
      <QuestionnaireCheckboxField
        form={form}
        label={label}
        parentGroup={group}
        group={questionnaireItem}
        groupTitleOrder={undefined}
        name={formFieldName || name}
        questionnaireResponse={response}
        canShowControlledFormset={false}
        parentPath={formFieldName?.split(".").slice(0, -1).join(".")}
      />
    );

  if (formControlType === FormControlType.RadioButton && !questionnaireItem.answerValueSet)
    return (
      <QuestionnaireRadioInputField
        form={form}
        label={label}
        response={response}
        initial={undefined}
        item={questionnaireItem}
        name={formFieldName || name}
        onChangeAnswer={onChangeAnswer}
      />
    );

  if (formControlType === FormControlType.Switch && !questionnaireItem.answerValueSet)
    return (
      <QuestionnaireChoiceSwitchField
        name={name}
        form={form}
        label={label}
        response={response}
        initial={undefined}
        item={questionnaireItem}
        onChangeAnswer={onChangeAnswer}
      />
    );

  if (formControlType === FormControlType.CheckBox && questionnaireItem.answerValueSet) {
    return (
      <QuestionnaireValueSetCheckboxField
        form={form}
        label={label}
        group={questionnaireItem}
        name={formFieldName || name}
      />
    );
  }

  if (formControlType === FormControlType.NativeSelect && questionnaireItem.answerValueSet) {
    return (
      <QuestionnaireNativeDropdown
        form={form}
        label={label}
        name={computedName}
        group={questionnaireItem}
        placeholder={placeholder}
        required={questionnaireItem.required ?? false}
        data-testid={questionnaireFieldTestId(computedName)}
      />
    );
  }

  if (formControlType === FormControlType.SignatureSelect) {
    return (
      <QuestionnaireSignatureSelectField
        form={form}
        name={name}
        label={label}
        placeholder={placeholder}
      />
    );
  }

  if (questionnaireItem.answerValueSet && hasCodingInputDetails) {
    return (
      <CodingInputWithDetails
        form={form}
        label={label}
        name={formFieldName ?? name}
        filterOption={codingFilter?.filterOption}
        valueSetUrl={questionnaireItem.answerValueSet}
        filteredOutMessage={codingFilter?.filteredOutMessage}
        data-testid={questionnaireFieldTestId(formFieldName ?? name)}
      />
    );
  }

  if (questionnaireItem.answerValueSet) {
    const onChange = (code: ValueSetExpansionContains | undefined) => {
      if (code === undefined) {
        form.getInputProps(formFieldName ?? name).onChange(null);
        return;
      }

      onChangeAnswer(code as ValueSetExpansionContains);
    };

    const useFormControlledValue = FORM_CONTROLLED_VALUE_SET_LINK_IDS.has(questionnaireItem.linkId);

    return (
      <ValueSetSearchInput
        preload
        name={name}
        label={label}
        onChange={onChange}
        creatable={!!valueSetIsCreatable}
        binding={questionnaireItem.answerValueSet}
        data-testid={questionnaireFieldTestId(formFieldName ?? name)}
        {...(useFormControlledValue
          ? { value: value as ValueSetExpansionContains | null | undefined }
          : { defaultValue: getCurrentAnswer(response)?.value || value })}
      />
    );
  }

  return (
    <QuestionnaireRadioInputField
      form={form}
      label={label}
      response={response}
      initial={undefined}
      item={questionnaireItem}
      name={formFieldName || name}
      onChangeAnswer={onChangeAnswer}
    />
  );
}

function getItemValue(answer: QuestionnaireResponseItemAnswer): TypedValue {
  const itemValue = getTypedPropertyValue(
    { type: "QuestionnaireItemAnswer", value: answer },
    "value",
  ) as TypedValue;
  return itemValue;
}

function getCurrentAnswer(
  response: QuestionnaireResponseItem | undefined,
  index: number = 0,
): TypedValue {
  const results = response?.answer;
  return getItemValue(results?.[index] ?? {});
}

function QuestionnaireChoiceSwitchField<T>(props: QuestionnaireChoiceInputProps<T>): JSX.Element {
  const { onChangeAnswer, label, response, item, form, name } = props;
  const trueOption = item?.answerOption?.[0]?.valueCoding;
  const falseOption = item?.answerOption?.[1]?.valueCoding;
  const currentOption = response?.answer?.[0]?.valueCoding?.code === trueOption?.code;

  // Get the current form value - this will be set during form initialization for switch fields
  const formValue = form.values[name as keyof T] as { code?: string; display?: string } | undefined;
  const formValueCode = formValue?.code;
  const isCheckedFromForm = formValueCode === trueOption?.code;

  // Use form value if available, otherwise fall back to response value
  const [checked, setChecked] = useState(
    formValueCode !== undefined ? isCheckedFromForm : currentOption,
  );

  const relatedFields = getExtensionValues(
    item as unknown as ExtensibleResource,
    "http://quiltedhealth.com/sd/questionnaire-field-related",
  ) as string[] | undefined;

  // Sync checked state with form value when form value changes
  useEffect(() => {
    if (formValueCode !== undefined) {
      const newChecked = formValueCode === trueOption?.code;
      if (newChecked !== checked) {
        setChecked(newChecked);
      }
    }
  }, [formValueCode, trueOption?.code, checked]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.checked;
    setChecked(newValue);
    const selectedCoding = newValue ? trueOption : falseOption;
    if (selectedCoding) {
      onChangeAnswer(selectedCoding);
      relatedFields?.forEach((field) => {
        if (
          (newValue && field.includes("Quantitative")) ||
          (!newValue && field.includes("Estimated"))
        ) {
          setFormFieldValue(form, field, "");
        }
      });
    }
  };
  return (
    <Switch
      label={label}
      checked={checked}
      onChange={handleChange}
      data-testid={questionnaireFieldTestId(name)}
    />
  );
}

export default QuestionnaireChoiceField;
